/**
 * UC-B-1 (UI-16, D4; Final PRD §19–21, §25, §59) — card indicators derived only from S2–S4 data carried by the board
 * (`segment_summary`, `shared_group_id`, `open_coverage`, `lock_reason`, `intentional_overlap_group_id`).
 * Never from support_ratio / linked_participants; never any billing status. Pure (Jest-tested).
 */
import { shiftSpan, toMinutes } from './shiftSheetRules';
import { lockMessage } from './segmentRules';

const PALETTE = ['bg-sky-400', 'bg-emerald-400', 'bg-violet-400', 'bg-amber-400', 'bg-rose-400', 'bg-teal-400', 'bg-indigo-400', 'bg-lime-500'];
export const serviceColour = (service) => {
  let h = 0;
  for (const ch of String(service || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

/** Thin colour bar: one block per segment, positioned on the shift's own window; empty array when no segments. */
export const segmentBar = (shift) => {
  const { start: S, end: E } = shiftSpan(shift);
  const span = Math.max(1, E - S);
  return (shift?.segment_summary || []).map((seg) => {
    let s = toMinutes(seg.start_time) ?? S; let e = toMinutes(seg.end_time) ?? s;
    if (s < S) s += 1440;
    if (e <= s) e += 1440;
    const left = Math.max(0, Math.min(100, ((s - S) / span) * 100));
    const width = Math.max(1, Math.min(100 - left, ((e - s) / span) * 100));
    // client#14: the hover text shows time + ratio only (no support item number); colour still tells segments apart
    return { service: seg.service || 'Service', ratio: seg.ratio || '', left, width, colour: serviceColour(seg.service), title: `${seg.start_time}–${seg.end_time}${seg.ratio ? ` · ${seg.ratio}` : ''}` };
  });
};

export const openCoverageText = (shift) => {
  const open = shift?.open_coverage || [];
  if (!open.length) return null;
  // UA-6-F19: ungrouped shift planned above 1:1 (server-derived workers_required from S2 segments) → "1 of 2 workers"
  const need = Number(shift?.workers_required) || 0; const have = Number(shift?.workers_assigned) || 0;
  const under = need > 1 && have < need ? ` · ${have} of ${need} workers` : '';
  return `Open ${open.map((o) => `${o.start_time}–${o.end_time}`).join(', ')}${under}`;
};

export const cardIndicators = (shift, conflict = null) => ({
  segments: segmentBar(shift),
  group: shift?.shared_group_id
    ? { id: shift.shared_group_id, label: shift.group_delivery_type === 'SIL_HOUSE' ? 'SIL house' : 'Shared', count: shift.group_member_count || 0, title: 'Shared delivery group — open the group sheet' }
    : null,
  open: openCoverageText(shift),
  overlap: conflict
    ? { kind: 'CONFLICT', tone: 'danger', label: 'Conflict', title: conflict.reason || 'Worker on an unrelated overlapping shift' }
    : (shift?.intentional_overlap_group_id ? { kind: 'INTENTIONAL', tone: 'info', label: 'Shared', title: 'Overlapping shifts delivered together in a shared delivery group — intentional, not a conflict' } : null),
  locked: shift?.lock_reason ? { reason: shift.lock_reason, title: lockMessage(shift.lock_reason) } : null,
});
